import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { URL, getToken } from "../utils";

export default function PaymentDeclarationModal({
  isOpen,
  onClose,
  clientId,
  resteAPayer,
  onPaymentSuccess,
}) {
  const [montant, setMontant] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateMontant = useCallback(
    (value) => {
      const newErrors = {};

      if (!value || value.trim() === "") {
        newErrors.montant = "Le montant est requis";
      } else {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          newErrors.montant = "Le montant doit être un nombre valide";
        } else if (numValue <= 0) {
          newErrors.montant = "Le montant doit être supérieur à 0";
        } else if (numValue > resteAPayer) {
          newErrors.montant = `Le montant ne peut pas dépasser ${resteAPayer?.toLocaleString()} FCFA`;
        }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [resteAPayer]
  );

  const handleMontantChange = (e) => {
    const value = e.target.value;
    setMontant(value);
    if (value) {
      validateMontant(value);
    } else {
      setErrors({});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateMontant(montant)) {
      return;
    }

    try {
      setLoading(true);
      const token = getToken();

      const response = await axios.post(
        `${URL}Clients/professionnels-declarer-paiement/${clientId}`,
        { montant: parseFloat(montant) },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      // Créer un lien de téléchargement pour le reçu PDF
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `recu_paiement_${clientId}_${new Date().toISOString().split("T")[0]}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(
        `Paiement de ${parseFloat(
          montant
        ).toLocaleString()} FCFA déclaré avec succès. Le reçu a été téléchargé.`
      );
      setMontant("");
      setErrors({});
      onPaymentSuccess?.();
      onClose();
    } catch (error) {
      console.error("Erreur lors de la déclaration du paiement:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Erreur lors de la déclaration du paiement";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMontant("");
    setErrors({});
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const isFormValid = montant && !errors.montant && parseFloat(montant) > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-400 rounded-full">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white">
              Déclarer un paiement
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-amber-100 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Affichage du reste à payer */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Montant à payer</p>
            <p className="text-2xl font-bold text-blue-700">
              {resteAPayer?.toLocaleString()} FCFA
            </p>
          </div>

          {/* Input montant */}
          <div>
            <label
              htmlFor="montant"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Montant à déclarer
            </label>
            <div className="relative">
              <input
                type="number"
                id="montant"
                value={montant}
                onChange={handleMontantChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                max={resteAPayer}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors.montant
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-amber-500"
                }`}
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                FCFA
              </span>
            </div>
            {errors.montant && (
              <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                {errors.montant}
              </p>
            )}
          </div>

          {/* Info message */}
          {montant && parseFloat(montant) > 0 && !errors.montant && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-700">
                Nouveau reste à payer:{" "}
                <span className="font-semibold">
                  {(resteAPayer - parseFloat(montant)).toLocaleString()} FCFA
                </span>
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!isFormValid || loading}
              className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Traitement...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Déclarer le paiement
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
